import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const { FiX, FiHeart, FiCoffee, FiCheck, FiExternalLink } = FiIcons;

const SupportUsModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const [selectedType, setSelectedType] = useState('oneTime'); // 'oneTime' or 'monthly'

  // 防止背景滾動
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // 重置選擇
  useEffect(() => {
    if (isOpen) {
      setSelectedType('oneTime');
    }
  }, [isOpen]);

  const paymentOptions = [
    { 
      amount: 5, 
      label: '$5',
      oneTimeLink: import.meta.env.VITE_STRIPE_ONE_TIME_5,
      monthlyLink: import.meta.env.VITE_STRIPE_MONTHLY_5,
      emoji: '🐰',
      tier: t('support.bunnySupport')
    },
    { 
      amount: 8, 
      label: '$8',
      oneTimeLink: import.meta.env.VITE_STRIPE_ONE_TIME_8,
      monthlyLink: import.meta.env.VITE_STRIPE_MONTHLY_8,
      emoji: '🐱',
      tier: t('support.kittenSupport')
    },
    { 
      amount: 10, 
      label: '$10',
      oneTimeLink: import.meta.env.VITE_STRIPE_ONE_TIME_10,
      monthlyLink: import.meta.env.VITE_STRIPE_MONTHLY_10,
      emoji: '🐶',
      tier: t('support.puppySupport')
    }
  ];

  const handleSupport = (option) => {
    const link = selectedType === 'oneTime' ? option.oneTimeLink : option.monthlyLink;
    
    if (!link || link.includes('your-link') || link.includes('your-monthly-link')) {
      alert(t('support.linkNotConfigured'));
      return;
    }

    // 🎯 不自動關閉 Popup，提升 UX
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-pink-500 via-rose-500 to-red-500 p-6 text-white">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="h-5 w-5" />
            </button>
            
            <div className="text-center">
              <div className="mb-4">
                <SafeIcon icon={FiHeart} className="h-16 w-16 mx-auto animate-pulse" />
              </div>
              <h2 className="text-3xl font-bold mb-2">{t('support.title')}</h2>
              <p className="text-white/90 text-sm leading-relaxed">{t('support.subtitle')}</p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Type Selector */}
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedType('oneTime')}
                className={`flex-1 py-4 px-4 rounded-xl font-semibold transition-all ${
                  selectedType === 'oneTime'
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <SafeIcon icon={FiCoffee} className="h-5 w-5" />
                  <span>{t('support.oneTime')}</span>
                </div>
              </button>
              
              <button
                onClick={() => setSelectedType('monthly')}
                className={`flex-1 py-4 px-4 rounded-xl font-semibold transition-all ${
                  selectedType === 'monthly'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <SafeIcon icon={FiHeart} className="h-5 w-5" />
                  <span>{t('support.monthly')}</span>
                </div>
              </button>
            </div>

            {/* Description */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm text-gray-700 text-center leading-relaxed">
                {selectedType === 'oneTime' 
                  ? t('support.oneTimeDescription')
                  : t('support.monthlyDescription')
                }
              </p>
            </div>

            {/* Amount Options */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800 text-center">
                {t('support.chooseAmount')}
              </h3>
              
              {paymentOptions.map((option) => (
                <motion.button
                  key={option.amount}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSupport(option)}
                  className="w-full p-4 rounded-xl border-2 border-gray-200 hover:border-pink-300 hover:bg-pink-50/50 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-4xl">{option.emoji}</div>
                      <div className="text-left">
                        <div className="text-2xl font-bold text-gray-900">
                          {option.label}
                          {selectedType === 'monthly' && (
                            <span className="text-base text-gray-500 font-normal ml-1">
                              {t('support.month')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{option.tier}</div>
                      </div>
                    </div>
                    <SafeIcon icon={FiExternalLink} className="h-5 w-5 text-gray-400" />
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Footer Note */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-800 text-center flex items-center justify-center gap-1">
                <SafeIcon icon={FiCheck} className="h-4 w-4" />
                <span>{t('support.securePayment')}</span>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SupportUsModal;